import { FC, useCallback, useEffect, useRef } from 'react';

import {
  Thumb,
  ThumbsList,
  ThumbsListItem,
} from '@/features/media/ui/FileManager/ThumbnailsList.styles';
import { isObjectNotEmpty } from '@/shared/lib/files';

interface Props {
  files: File[];
  activeFileIndex: number;
  onClick: (index: number) => void;
}

const ThumbnailsList: FC<Props> = ({ files, activeFileIndex, onClick }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const target = targetRef.current;

    if (container && target) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      const isFullyVisible =
        targetRect.left >= containerRect.left && targetRect.right <= containerRect.right;

      if (!isFullyVisible) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [activeFileIndex]);

  const handleClick = useCallback(
    (index: number) => {
      if (activeFileIndex !== index) {
        onClick(index);
      }
    },
    [activeFileIndex, onClick],
  );

  return (
    <ThumbsList ref={containerRef}>
      {files.map((item: File, index: number) =>
        item ? (
          <ThumbsListItem
            key={index}
            ref={index === activeFileIndex ? targetRef : null}
            onClick={() => handleClick(index)}
            active={activeFileIndex === index}
          >
            <Thumb
              src={isObjectNotEmpty(item) ? URL.createObjectURL(item) : undefined}
              alt={item?.name ?? 'image'}
              loading="lazy"
              draggable="false"
            />
          </ThumbsListItem>
        ) : null,
      )}
    </ThumbsList>
  );
};

export default ThumbnailsList;
